'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import NextLink from 'next/link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Box, Button, CircularProgress, Stack, Typography } from '@/primitives';
import { formatLotPrice } from '@/lib/formatLotPrice';
import {
  buildSaleLotHref,
  buildSaleListingHref,
} from '@/lib/saleCataloguePaths';
import { useSaleLotNavigation } from '@/hooks/publicSearch/sale-catalogue/useSaleLotNavigation';
import type { SaleLotNavTarget } from '@/hooks/publicSearch/sale-catalogue/saleLotNavigationUtils';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { LotVendorLogo, useLotVendorLogoSrc } from './LotVendorLogo';

type LotContextBarProps = {
  orgCode: string;
  saleId: string;
  lotNo: string;
  /** Preloaded by the catalogue provider so lot name can also override the animal header. */
  navigation: ReturnType<typeof useSaleLotNavigation>;
};

const LOT_DETAILS_EXPANDED_KEY = 'saleLotDetailsExpanded';
/** Approx. extra width for "Back to Lot Listing" vs "Back" at 0.75rem. */
const LONG_BACK_LABEL_EXTRA_PX = 95;
const TOOLBAR_COLUMN_GAP_PX = 12;

function readLotDetailsExpanded(saleId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = sessionStorage.getItem(LOT_DETAILS_EXPANDED_KEY);
    if (!stored) return true;
    const parsed = JSON.parse(stored) as Record<string, boolean>;
    return parsed[saleId] ?? true;
  } catch {
    return true;
  }
}

function writeLotDetailsExpanded(saleId: string, expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    const stored = sessionStorage.getItem(LOT_DETAILS_EXPANDED_KEY);
    const parsed = stored
      ? (JSON.parse(stored) as Record<string, boolean>)
      : {};
    parsed[saleId] = expanded;
    sessionStorage.setItem(LOT_DETAILS_EXPANDED_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage errors
  }
}

function LotTitle({
  lotNo,
  saleName,
}: {
  lotNo: string;
  saleName?: string | null;
}) {
  return (
    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
      Lot {lotNo.trim()}
      {saleName ? ` · ${saleName}` : ''}
    </Typography>
  );
}

export default function LotContextBar({
  orgCode,
  saleId,
  lotNo,
  navigation,
}: LotContextBarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLDivElement>(null);
  const pagingRef = useRef<HTMLDivElement>(null);
  const [useShortBackLabel, setUseShortBackLabel] = useState(false);

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrates the expanded state from sessionStorage (browser-only, unavailable during SSR render) when the sale changes; detailsExpanded is also set by handleDetailsExpandedChange
    setDetailsExpanded(readLotDetailsExpanded(saleId));
  }, [saleId]);

  const handleDetailsExpandedChange = (
    _event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setDetailsExpanded(isExpanded);
    writeLotDetailsExpanded(saleId, isExpanded);
  };

  const navigateToLot = (target: SaleLotNavTarget) => {
    const tab = searchParams.get('tab');
    router.push(
      buildSaleLotHref(orgCode, {
        saleId,
        lotNo: target.lotNo,
        animalId: target.animalId,
        searchParams,
        tab,
      }),
    );
  };

  const priceLabel = formatLotPrice(
    navigation.currentLot?.askingPrice ?? navigation.currentLot?.salePrice,
    navigation.currentLot?.askingPriceDesc ??
      navigation.currentLot?.salePriceDesc,
  );
  // BLS-382: many societies' lots carry no vendor key; the seller is then the
  // current owner (i4 shows that member's stud logo).
  const vendorName = (
    navigation.currentLot?.vendorsName ??
    navigation.currentLot?.currentOwnerName ??
    ''
  ).trim();
  const orgId = usePublicSearchContext((c) => c.society.orgId);
  const vendorLogoSrc = useLotVendorLogoSrc(
    {
      vendorsId: navigation.currentLot?.vendorsId,
      currentOwnerId: navigation.currentLot?.currentOwnerId,
    },
    orgId,
  );
  const lotNotes = navigation.currentLot?.lotNotes?.trim();
  const hasLotNotes = Boolean(lotNotes);
  // BLS-382: a vendor logo can exist even when i4 left the vendor name blank
  // (e.g. AI sire lists); show the block when either is available.
  const hasVendor = Boolean(vendorName) || Boolean(vendorLogoSrc);
  const hasExpandableContent = Boolean(priceLabel || hasVendor || hasLotNotes);

  const priceText =
    priceLabel &&
    (navigation.currentLot?.salePrice != null &&
    !navigation.currentLot?.askingPrice
      ? `Sold: ${priceLabel}`
      : `Asking: ${priceLabel}`);

  const showLotPaging = navigation.loading || navigation.canNavigate;

  const backHref = useMemo(
    () => buildSaleListingHref(orgCode, saleId, searchParams),
    [orgCode, saleId, searchParams],
  );

  useLayoutEffect(() => {
    if (!showLotPaging) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the label state when paging is hidden, within a layout effect that otherwise measures DOM widths and subscribes a ResizeObserver to drive useShortBackLabel
      setUseShortBackLabel(false);
      return;
    }

    const toolbar = toolbarRef.current;
    const backButton = backButtonRef.current;
    const paging = pagingRef.current;
    if (!toolbar || !backButton || !paging) return;

    const updateBackLabel = () => {
      const toolbarWidth = toolbar.clientWidth;
      const pagingWidth = paging.offsetWidth;
      const currentBackWidth = backButton.offsetWidth;

      setUseShortBackLabel((isShort) => {
        const backWidthIfLong = isShort
          ? currentBackWidth + LONG_BACK_LABEL_EXTRA_PX
          : currentBackWidth;
        const fitsLongLabel =
          backWidthIfLong + TOOLBAR_COLUMN_GAP_PX + pagingWidth <= toolbarWidth;
        return !fitsLongLabel;
      });
    };

    updateBackLabel();

    const observer = new ResizeObserver(updateBackLabel);
    observer.observe(toolbar);

    return () => observer.disconnect();
  }, [
    showLotPaging,
    navigation.loading,
    navigation.canNavigate,
    navigation.positionLabel,
    useShortBackLabel,
  ]);

  const backLabel = useShortBackLabel ? 'Back' : 'Back to Lot Listing';

  return (
    <Box sx={{ mr: '10px' }}>
      <Stack
        ref={toolbarRef}
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: '12px',
          rowGap: '6px',
          minHeight: '36px',
          mt: '8px',
          pt: 0.75,
          mb: 1.5,
        }}
      >
        <Box ref={backButtonRef} sx={{ flexShrink: 0 }}>
          <Button
            component={NextLink}
            href={backHref}
            size="small"
            aria-label="Back to lot listing"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} aria-hidden />}
            sx={{
              width: { xs: 'auto', sm: 'min-content' },
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              py: 0.25,
              px: 1,
              minHeight: 30,
              '& .MuiButton-startIcon': { mr: 0.25, ml: -0.25 },
            }}
            variant="outlined"
          >
            {backLabel}
          </Button>
        </Box>

        {showLotPaging && (
          <Stack
            ref={pagingRef}
            direction="row"
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              ml: 'auto',
            }}
          >
            {navigation.loading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!navigation.prev}
                  onClick={() =>
                    navigation.prev && navigateToLot(navigation.prev)
                  }
                  startIcon={<ChevronLeftIcon />}
                  sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                >
                  Previous Lot
                </Button>
                {navigation.positionLabel && (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      px: 0.5,
                    }}
                  >
                    {navigation.positionLabel}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!navigation.next}
                  onClick={() =>
                    navigation.next && navigateToLot(navigation.next)
                  }
                  endIcon={<ChevronRightIcon />}
                  sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                >
                  Next Lot
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {hasExpandableContent ? (
          <Accordion
            expanded={detailsExpanded}
            onChange={handleDetailsExpandedChange}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                px: 2,
                '& .MuiAccordionSummary-content': { my: 1, minWidth: 0 },
              }}
            >
              <LotTitle lotNo={lotNo} saleName={navigation.saleName} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              {priceText && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    mb: hasVendor || hasLotNotes ? 1.5 : 0,
                  }}
                >
                  {priceText}
                </Typography>
              )}
              {hasVendor && (
                <Box
                  sx={{
                    mb: hasLotNotes ? 1.5 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}
                    >
                      Vendor
                    </Typography>
                    {vendorName && (
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                        }}
                      >
                        {vendorName}
                      </Typography>
                    )}
                  </Box>
                  {/* BLS-382: vendor (stud) logo as shown on i4 sale catalogues. */}
                  {vendorLogoSrc && (
                    <LotVendorLogo
                      src={vendorLogoSrc}
                      vendorName={vendorName}
                    />
                  )}
                </Box>
              )}
              {hasLotNotes && (
                <Box>
                  <Typography
                    sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}
                  >
                    Lot Notes
                  </Typography>
                  <Typography
                    variant="bodysmall"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: lotNotes! }}
                    sx={{
                      color: 'text.secondary',
                      '& p': { m: 0 },
                      '& p + p': { mt: 0.5 },
                      '& ul, & ol': { pl: 2.5, m: 0 },
                      '& a': { color: 'primary.main' },
                    }}
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ) : (
          <Box sx={{ px: 2, py: 1.5 }}>
            <LotTitle lotNo={lotNo} saleName={navigation.saleName} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
